// Adds comment toggling and auto-indenting
import "prism-react-editor/languages/json";

import "prism-react-editor/layout.css";
import "prism-react-editor/themes/github-dark.css";

// Required by the basic setup
import "prism-react-editor/search.css";

import { useMutation } from "@tanstack/react-query";
import { Editor } from "prism-react-editor";
import { BasicSetup } from "prism-react-editor/setups";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useMatches } from "react-router-dom";
import { useTemplateTestFields } from "../../hook/template-test-fields";
import { getLiveLanguages, languages } from "../../models/company";
import { useAppSelector } from "../../store";
import { companyActions } from "../../store/company-slice";
import { testMailTemplate } from "../../utils/http/mail-templates";
import "./Template.css";

function SendTestMailModal({
  showSendTestModal,
  setShowSendTestModal,
  bulkTestPaths = [],
}: {
  showSendTestModal: any;
  setShowSendTestModal: any;
  bulkTestPaths?: any[];
}) {
  const dispatch = useDispatch();
  // This modal is rendered by the mail-templates parent route, whose own match carries no
  // params, so useParams() here would be empty. The leaf match is the template route.
  const matches = useMatches();
  const { automation, event, action, target, version } = matches[matches.length - 1]?.params ?? {};

  const activeCompany = useAppSelector((state) => state.company.activeCompany);

  const testData = useAppSelector((state) => state.company.mailTemplateTestData);

  // Tracks the route's template even while the modal is shut, so opening it never shows the
  // generic fields first. Bulk spans many templates, so it has no key and falls back to them.
  const templateKey =
    showSendTestModal !== "BULK" && automation && event && action && target
      ? `${automation}.${event}.${action}.${target}`
      : null;
  const { fields, datasets, datasetName, editKey, setDatasetName, setEditedText, error } =
    useTemplateTestFields(templateKey);

  // The editor is uncontrolled: `value` seeds it, and setting that prop resets the caret to
  // the top, so this must NOT follow what the user types. It changes only on a different
  // template or dataset, where the edits no longer apply and a reseed is what we want.
  const [editorSeed, setEditorSeed] = useState("");

  useEffect(() => {
    setEditorSeed(JSON.stringify(fields, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editKey]);

  // Typing feeds the preview and the send payload, never the seed above. prism-react-editor
  // also fires this when `value` is set programmatically, so it echoes our own reseed back;
  // that is harmless, since the text is simply stored as typed.
  const onEditorUpdate = (text: string) => setEditedText(text);

  // Send testmail
  const { mutate: mutateTestMailTemplate } = useMutation({
    mutationFn: testMailTemplate,
    onSuccess: () => { },
  });

  const sendTestMail = async () => {
    const payload: any = {
      languages: testData.languages,
      fields,
    };
    if (showSendTestModal === "SINGLE") {
      payload["paths"] = [{ event, action, target, version }];
    }
    if (showSendTestModal === "BULK") {
      payload["paths"] = bulkTestPaths.map((t) => ({
        event: t.event,
        action: t.action,
        target: t.target,
        version: t.version,
      }));
    }

    await mutateTestMailTemplate(payload);

    setShowSendTestModal("HIDDEN");
  };

  const closeSendTestMailModal = () => {
    setShowSendTestModal("HIDDEN");
  };

  const updateTestData = (data: any) => {
    dispatch(companyActions.setMailTemplateTestData({ ...testData, ...data }));
  };

  return (
    <Modal size="xl" show={showSendTestModal !== "HIDDEN"} onHide={closeSendTestMailModal}>
      <Modal.Header closeButton>
        <Modal.Title>Send Test Mail</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row>
            <Col xs={6}>
              {showSendTestModal === "BULK" && (
                <Form.Group as={Row} className="mb-3 align-items-center">
                  <Form.Label column sm="12">
                    {bulkTestPaths.length} selected flow(s)
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id={`tooltip-bottom`}>
                          Test mails are only sent for the flows you selected in the summary.
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-1"></i>
                    </OverlayTrigger>
                  </Form.Label>
                </Form.Group>
              )}
              {showSendTestModal === "SINGLE" && datasets.length > 1 && (
                <Form.Group as={Row} className="mb-3 align-items-center">
                  <Form.Label column sm="4">
                    Dataset
                  </Form.Label>
                  <Col sm="8">
                    <Form.Select value={datasetName ?? ""} onChange={(e) => setDatasetName(e.target.value)}>
                      {datasets.map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.name}
                          {d.is_default ? " (default)" : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Form.Group>
              )}
            </Col>
            <Col xs={1} />
            <Col xs={5}>
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Col sm="12" className="text-end">
                  <Form.Label column sm="4">
                    &nbsp; Languages&nbsp;
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id={`tooltip-bottom`}>For which languages should we send out test mails?</Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-1"></i>
                    </OverlayTrigger>
                  </Form.Label>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  {activeCompany &&
                    getLiveLanguages(activeCompany?.info?.languages).map((l) => {
                      const language = languages.find((lang) => lang.id === l);
                      return (
                        <Form.Check
                          type="checkbox"
                          key={`language-${l}`}
                          checked={testData?.languages.includes(l)}
                          onChange={() => {
                            if (testData.languages.includes(l)) {
                              const languages = [...testData.languages.filter((language) => language !== l)];
                              updateTestData({ languages });
                            } else {
                              updateTestData({
                                languages: [...testData.languages.filter((language) => language !== l), l],
                              });
                            }
                          }}
                          inline
                          label={language?.name || l}
                        />
                      );
                    })}
                </Col>
              </Form.Group>
            </Col>
          </Row>
          {!!error && (
            <Alert variant="warning">
              Could not load this template's variables; showing the generic test fields instead.
            </Alert>
          )}
          <Row>
            <Col>
              <Editor
                language="jsx"
                value={editorSeed}
                onUpdate={onEditorUpdate}
                wordWrap={false}
              >
                {(editor) => <BasicSetup editor={editor} />}
              </Editor>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeSendTestMailModal}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={sendTestMail}
          disabled={showSendTestModal === "BULK" && bulkTestPaths.length === 0}
        >
          Send Test Mail
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SendTestMailModal;
